import {
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, tap } from "rxjs/operators";
import Quill, { Delta } from "quill";
import { PlaceholderBlot } from "./placeholder-module/placeholder-blot";
import { DivBlot } from "./div-blot";
import { QuillEditorComponent } from "ngx-quill";
import { placeholderLabel } from "./config/placeholder.const";

@Component({
  selector: "app-wysiwyg",
  templateUrl: "./wysiwyg.component.html",
  styleUrls: ["./wysiwyg.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [QuillEditorComponent],
  host: { "[class.with-toolbar]": "showToolbarItems()" },
})
export class WysiwygComponent implements OnInit, OnChanges, OnDestroy {
  public config: any;
  public wysiwygValue = "";
  public quillInstance: any;

  public readonly showPlaceholders = input(false);
  public readonly showToolbarItems = input(true);
  public readonly value = input("");
  public readonly readonly = input(false);
  public readonly minHeight = input(false);
  public readonly placeholderValues = input<Record<string, string> | null>(
    null,
  );
  // The placeholders the picker offers; every one when not set.
  public readonly placeholderIds = input<readonly string[] | null>(null);

  public readonly valueChanged = output<string>();

  private valueChangedSubject: Subject<string> = new Subject<string>();
  private pendingChange: string | null = null;
  private pendingValuesRefresh = false;
  private removePasteGuard: (() => void) | null = null;
  private removeArrowGuard: (() => void) | null = null;
  private removeResizeGuard: (() => void) | null = null;

  constructor() {
    this.valueChangedSubject
      .pipe(
        distinctUntilChanged(),
        tap((value) => (this.pendingChange = value)),
        debounceTime(400),
      )
      .subscribe(() => this.flushChange());
    Quill.register(DivBlot, true);
    // Overwrite explicitly: the constructor runs once per editor instance, and
    // without the flag Quill warns and dumps the whole blot class on every
    // re-registration after the first.
    Quill.register(PlaceholderBlot, true);
    registerHistoryIcons();
  }

  ngOnInit() {
    this.config = {
      table: false,
      "table-better": {
        language: "en_US",
        menus: [
          "column",
          "row",
          "merge",
          "table",
          "cell",
          "wrap",
          "copy",
          "delete",
        ],
        toolbarTable: true,
      },
      toolbar: {
        container: [],
        handlers: {
          redo() {
            this.quill.history.redo();
          },
          undo() {
            this.quill.history.undo();
          },
        },
      },
      clipboard: {
        matchVisual: false,
      },
      history: { delay: 500, maxStack: 500, userOnly: true },
    };

    // Quill instantiates every module key it is handed, `false` included, so a readonly editor
    // only stays free of drag handles if the key is left off altogether.
    if (!this.readonly()) {
      this.config.resize = {
        modules: ["DisplaySize", "Resize", "Keyboard"],
        // Re-injects its arrow-key bindings on every image selection, so leave it off.
        keyboardSelect: false,
        // Videos and iframes get a pointer-events:none overlay we do not want in the email editors.
        embedTags: [],
        // Width alone keeps the aspect ratio, and the module writes it as an attribute - that is
        // what survives the backend sanitizer and reaches the quote PDF.
        parchment: {
          image: { attribute: ["width"], limit: { minWidth: 24 } },
        },
      };
    }

    if (this.showToolbarItems()) {
      this.config.toolbar.container = [
        ["undo", "redo"],
        ["bold", "italic", "underline", "strike", "clean"],
        [
          {
            size: [
              false,
              "10px",
              "16px",
              "22px",
              "36px",
              "48px",
              "72px",
              "144px",
            ],
          },
          { color: [] },
          { background: [] },
          { align: ["center", "", "right", "justify"] },
        ],
        ["link", "image", "table-better", "blockquote"],
        [
          { list: "ordered" },
          { list: "bullet" },
          { indent: "-1" },
          { indent: "+1" },
        ],
      ];
    }

    if (this.showPlaceholders()) {
      const offered = this.placeholderIds() ?? Object.keys(placeholderLabel);
      this.config.placeholder = {
        delimiters: ["[[", "]]"],
        values: this.placeholderValues() ?? {},
        placeholders: offered.map((id) => ({
          id,
          label: placeholderLabel[id] ?? id,
        })),
      };
      this.config.toolbar.container.push([{ placeholder: [...offered] }]);
    }
  }

  // Emits an edit still waiting out the debounce right away, so a host can commit it to what it
  // was typed for before switching the editor to other content.
  public flushChange(): void {
    if (this.pendingChange === null) {
      return;
    }

    const value = this.pendingChange;
    this.pendingChange = null;
    this.valueChanged.emit(this.stripPlaceholderSpan(value));
  }

  public updateValue(value: string, silent = false): void {
    this.applyPlaceholderValues();
    this.wysiwygValue = this.addPlaceholderSpan(value);
    this.initValue(
      this.quillInstance,
      this.wysiwygValue,
      silent ? Quill.sources.API : Quill.sources.USER,
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.value && changes.value.firstChange) {
      this.wysiwygValue = changes.value.currentValue
        ? this.addPlaceholderSpan(changes.value.currentValue)
        : "";
    }

    // Resolved values arrive after the editor is up, so re-render the content they belong to.
    // The refresh is silent, otherwise it would look like a user edit and trigger a save.
    if (changes.placeholderValues && !changes.placeholderValues.firstChange) {
      if (!this.quillInstance) {
        this.applyPlaceholderValues();
      } else if (this.quillInstance.hasFocus()) {
        // Re-rendering resets the caret, so wait until the user is done typing.
        this.pendingValuesRefresh = true;
      } else {
        this.renderPlaceholderValues();
      }
    }
  }

  private renderPlaceholderValues(): void {
    this.pendingValuesRefresh = false;
    this.updateValue(
      this.stripPlaceholderSpan(this.quillInstance.root.innerHTML),
      true,
    );
  }

  public onEditorCreated(quillInstance): void {
    this.quillInstance = quillInstance;
    this.applyPlaceholderValues();
    // The clipboard matchers go in first, so the first content is converted like every later one.
    this.initListeners(this.quillInstance);
    this.initValue(this.quillInstance, this.wysiwygValue, Quill.sources.API);
    this.labelPlaceholderPicker();
  }

  // Quill builds the picker items from the format values, so the ids are all it has to show -
  // the readable names have to be written onto the items once the toolbar exists.
  private labelPlaceholderPicker(): void {
    const toolbar = this.quillInstance?.getModule("toolbar");
    toolbar?.container
      ?.querySelectorAll(".ql-picker.ql-placeholder .ql-picker-item")
      .forEach((item: HTMLElement) => {
        const id = item.dataset.value ?? "";
        item.textContent = placeholderLabel[id] ?? id;
      });
  }

  // The blot renders from a static registry shared by every editor, so the values of this
  // instance have to be in place before anything gets rendered.
  private applyPlaceholderValues(): void {
    if (!this.showPlaceholders()) {
      return;
    }

    const values = this.placeholderValues() ?? {};
    PlaceholderBlot.values = values;
    if (this.config?.placeholder) {
      this.config.placeholder.values = values;
    }
  }

  private initValue(quillInstance, html: string, source: string): void {
    // The selected table cells are about to be replaced, and quill-table-better would keep them.
    quillInstance.getModule("table-better")?.cellSelection?.clearSelected();
    quillInstance.deleteText(0, quillInstance.getLength());
    const delta = quillInstance.clipboard.convert({ html });
    const [range] = quillInstance.selection.getRange();
    quillInstance.updateContents(delta, source);
    quillInstance.setSelection(
      delta.length() - range?.length,
      Quill.sources.SILENT,
    );
    quillInstance.scrollSelectionIntoView();
  }

  private initListeners(quillInstance): void {
    quillInstance.on("selection-change", (range) => {
      if (!range && this.pendingValuesRefresh) {
        this.renderPlaceholderValues();
      }
    });
    quillInstance.clipboard.addMatcher("table", (node: HTMLElement, delta) => {
      if (node.classList.contains("converted")) {
        return delta;
      } else {
        return quillInstance.clipboard.convert({
          html: this.markTableConverted(node),
        });
      }
    });
    quillInstance.clipboard.addMatcher("table, td, th", (node, delta) =>
      this.dropContainerBackground(node, delta),
    );
    this.initPasteGuard(quillInstance);
    this.initArrowGuard(quillInstance);
    this.initResizeGuard(quillInstance);
  }

  // A shallow clone keeps the table's own attributes - its style (fill, width) and border are
  // what quill-table-better renders the table with.
  private markTableConverted(table: HTMLElement): string {
    const converted = table.cloneNode(false) as HTMLElement;
    converted.classList.add("converted");
    converted.innerHTML = this.addPlaceholderSpan(table.innerHTML);

    return converted.outerHTML;
  }

  // Quill's clipboard copies a table's or cell's background-color onto every text run inside it,
  // so a filled table came back as highlighted text. The table or cell keeps the color in its own
  // style, which makes the copy on its runs redundant.
  private dropContainerBackground(node: HTMLElement, delta: Delta): Delta {
    const containerBackground = (
      Quill.import("formats/background") as {
        value(node: HTMLElement): string;
      }
    ).value(node);
    if (!containerBackground) {
      return delta;
    }

    return new Delta(
      delta.ops.map((op) => {
        if (op.attributes?.background !== containerBackground) {
          return op;
        }
        const attributes = { ...op.attributes };
        delete attributes.background;

        return { ...op, attributes };
      }),
    );
  }

  // quill-table-better keeps its cell selection when the cells are removed by anything else (a
  // table menu action, a content update), and its Up/Down keyup handler then looks the removed
  // cell up and throws. Its listener sits on the root, so a capture listener on the container
  // drops the stale selection first.
  private initArrowGuard(quillInstance): void {
    const cellSelection =
      quillInstance.getModule("table-better")?.cellSelection;
    if (!cellSelection) {
      return;
    }

    const isAttached = (td: Element | null): boolean =>
      !!td && quillInstance.root.contains(td) && !!Quill.find(td);
    const guard = (): void => {
      if (
        cellSelection.selectedTds.length &&
        !(isAttached(cellSelection.startTd) && isAttached(cellSelection.endTd))
      ) {
        cellSelection.clearSelected();
      }
    };
    quillInstance.container.addEventListener("keyup", guard, true);
    this.removeArrowGuard = () =>
      quillInstance.container.removeEventListener("keyup", guard, true);
  }

  // quill-table-better writes a table's new width onto the table's hidden width row and throws
  // when the table has none - on releasing the last column's border or the corner block. The throw
  // skips its mouseup cleanup, so the resize line keeps following the mouse. Such a table keeps its
  // width instead.
  private initResizeGuard(quillInstance): void {
    const operateLine = quillInstance.getModule("table-better")?.operateLine;
    if (!operateLine) {
      return;
    }

    const { setCellRect, setCellsRect } = operateLine;
    const lacksWidthRow = (cell: Element): boolean =>
      (Quill.find(cell) as any)?.table?.()?.temporary() == null;
    operateLine.setCellRect = (
      cell: Element,
      clientX: number,
      clientY: number,
    ): void => {
      if (
        operateLine.direction === "level" &&
        !cell.nextElementSibling &&
        lacksWidthRow(cell)
      ) {
        return;
      }
      setCellRect.call(operateLine, cell, clientX, clientY);
    };
    operateLine.setCellsRect = (
      cell: Element,
      changeX: number,
      changeY: number,
    ): void => {
      if (lacksWidthRow(cell)) {
        return;
      }
      setCellsRect.call(operateLine, cell, changeX, changeY);
    };
    this.removeResizeGuard = () => {
      operateLine.setCellRect = setCellRect;
      operateLine.setCellsRect = setCellsRect;
    };
  }

  // quill-table-better pastes into its selected cells from a document-wide listener, wherever the
  // paste lands, and keeps that listener after the editor is gone. Without a caret it can place it
  // throws, so drop the cell selection first and let the paste go where the user aimed it.
  // Selecting several cells blurs the editor, so a paste meant for them lands on the body - only a
  // paste into another editable field is aimed elsewhere. That is checked before the caret, because
  // reading the caret focuses the editor.
  private initPasteGuard(quillInstance): void {
    const cellSelection =
      quillInstance.getModule("table-better")?.cellSelection;
    if (!cellSelection) {
      return;
    }

    const guard = (event: ClipboardEvent): void => {
      if (
        cellSelection.selectedTds.length &&
        (!quillInstance.isEnabled() ||
          this.isOtherField(quillInstance, event.target) ||
          !quillInstance.getSelection(true))
      ) {
        cellSelection.clearSelected();
      }
    };
    document.addEventListener("paste", guard, true);
    this.removePasteGuard = () =>
      document.removeEventListener("paste", guard, true);
  }

  private isOtherField(quillInstance, target: EventTarget | null): boolean {
    return (
      target instanceof HTMLElement &&
      !quillInstance.root.contains(target) &&
      (target.isContentEditable || target.matches("input, textarea"))
    );
  }

  ngOnDestroy(): void {
    this.removePasteGuard?.();
    this.removeArrowGuard?.();
    this.removeResizeGuard?.();
    this.quillInstance
      ?.getModule("table-better")
      ?.cellSelection?.clearSelected();
  }

  public emitChange(event: any) {
    const data = event.html;
    if (
      this.wysiwygValue.localeCompare(data) !== 0 &&
      event.source === Quill.sources.USER
    ) {
      this.valueChangedSubject.next(data);
    }
  }

  private addPlaceholderSpan(value: string | null): string {
    if (!value) {
      return "";
    }

    value = value.replace(/\[\[(.+?)]]/gim, (match, id: string) => {
      const label = placeholderLabel[id] ?? id;

      return `<span class="ql-placeholder-content" data-id="${id}" data-label="${label}" spellcheck="false" contenteditable="false">${label}</span>`;
    });

    // Quill 2.0.3 has some issues with spaces (https://github.com/slab/quill/issues/4509)
    value = value.replace(/((?:&nbsp;)*)&nbsp;/gim, "$1 ");

    // Quill has some issues with table headers, so we need to replace <th> with <td> and add a <b> tag
    value = value.replace(/<th\b([^>]*)>/gim, "<td$1><b>");
    value = value.replace(/<\/th>/gim, "</b></td>");

    // Quill is merging tables when one is right after another one, that's why we need to add a <br> tag
    value = value.replace(/<figure/gim, "<p");
    value = value.replace(/<\/figure>/gim, "</p><br/>");

    return value;
  }

  private stripPlaceholderSpan(value: string): string {
    if (!value) {
      return "";
    }

    // Resolved placeholders hold arbitrary markup, so the token has to be recovered from data-id
    // over the parsed DOM - a regex cannot tell which </span> closes the placeholder.
    const container = document.createElement("div");
    container.innerHTML = value;
    container
      .querySelectorAll(".ql-placeholder-content")
      .forEach((node: Element) => {
        const id = (node as HTMLElement).dataset.id;
        node.replaceWith(
          document.createTextNode(id ? `[[${id}]]` : (node.textContent ?? "")),
        );
      });

    value = container.innerHTML;

    // Quill 2.0.3 has some issues with spaces (https://github.com/slab/quill/issues/4509)
    value = value.replace(/\u00a0/g, " ");
    value = value.replace(/((?:&nbsp;)*)&nbsp;/gim, "$1 ");

    return value;
  }
}

// Quill ships no glyph for undo and redo. Registered as inline SVG like every other toolbar icon
// so they take the same .ql-fill colors - a CSS mask over a filled button left a 1px unmasked
// edge whenever the toolbar landed on a fractional pixel. The viewBox margin keeps the 512 unit
// Font Awesome glyph at the ~14px of its neighbors inside Quill's 18px icon box.
// Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.
function registerHistoryIcons(): void {
  const icons = Quill.import("ui/icons") as Record<string, unknown>;
  icons["undo"] = historyIcon(
    "M212.3 224.3H12c-6.6 0-12-5.4-12-12V12C0 5.4 5.4 0 12 0h48c6.6 0 12 5.4 12 12v78.1C117.8 39.3 184.3 7.5 258.2 8c136.9 1 246.4 111.6 246.2 248.5C504 393.3 393.1 504 256.3 504c-64.1 0-122.5-24.3-166.5-64.2-5.1-4.6-5.3-12.6-.5-17.4l34-34c4.5-4.5 11.7-4.7 16.4-.5C170.8 415.3 211.6 432 256.3 432c97.3 0 176-78.7 176-176 0-97.3-78.7-176-176-176-58.5 0-110.3 28.5-142.3 72.3h98.3c6.6 0 12 5.4 12 12v48c0 6.6-5.4 12-12 12z",
  );
  icons["redo"] = historyIcon(
    "M500.3 0h-47.4a12 12 0 0 0 -12 12.6l4 82.8A247.4 247.4 0 0 0 256 8C119.3 8 7.9 119.5 8 256.2 8.1 393.1 119.1 504 256 504a247.1 247.1 0 0 0 166.2-63.9 12 12 0 0 0 .5-17.4l-34-34a12 12 0 0 0 -16.4-.6A176 176 0 1 1 402.1 157.8l-101.5-4.9a12 12 0 0 0 -12.6 12v47.4a12 12 0 0 0 12 12h200.3a12 12 0 0 0 12-12V12a12 12 0 0 0 -12-12z",
  );
}

function historyIcon(path: string): string {
  return `<svg viewBox="-72 -72 656 656"><path class="ql-fill" d="${path}"/></svg>`;
}
